import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { ApiService } from '../../core/services/api.service';
import { AuthService } from '../../core/services/auth.service';
import { DashboardStats, MemberPieStats, MemberTypeStat } from '../../core/models';
import { PageTitleService } from '../../core/services/page-title.service';
import { CanComponentDeactivate } from '../../core/guards/can-deactivate.guard';
import { MemberListPanelComponent } from '../../shared/components/member-list-panel/member-list-panel.component';

interface PieSegment {
  dash: string;
  offset: number;
  color: string;
  label: string;
  count: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, MemberListPanelComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, CanComponentDeactivate {
  stats: DashboardStats | null = null;
  isLoading = true;
  currentUser: any;

  memberPieStats: MemberPieStats | null = null;
  isLoadingPie = true;

  // Greeting + today's snapshot
  todaySnapshot: { issuedToday: number; returnedToday: number; transactionsToday: number; newMembersToday: number } | null = null;
  isLoadingSnapshot = true;

  get greeting(): string {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  }

  get todayLabel(): string {
    return new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }

  // Member Status Grouped Bar Chart
  readonly BAR_MAX_PX = 80;

  // Time-based pie charts (below the bar chart)
  readonly PIE_R = 28;
  readonly PIE_C = 2 * Math.PI * 28;

  readonly PIE_CHARTS: { key: keyof MemberPieStats; label: string; statType: string }[] = [
    { key: 'dueExpiredLast30Days', label: 'Turned Due (last 30 days)',  statType: 'notRenewedLast30Days' },
    { key: 'renewedLast30Days',    label: 'Renewed (last 30 days)',     statType: 'renewedLast30Days'    },
    { key: 'newMembersLast30Days', label: 'New Members (last 30 days)', statType: 'newMembersLast30Days' },
  ];

  readonly BAR_GROUPS: { key: keyof MemberPieStats; label: string; statType: string; colorClass: string }[] = [
    { key: 'activeMembers', label: 'Member',  statType: 'activeMembers', colorClass: 'green' },
    { key: 'dueMembers',    label: 'Due',     statType: 'dueMembers',    colorClass: 'red'   },
    { key: 'onHoldMembers', label: 'On Hold', statType: 'membersOnHold', colorClass: 'amber' },
    { key: 'closedMembers', label: 'Closed',  statType: 'closedMembers', colorClass: 'slate' },
  ];

  // Route stacked bar chart
  readonly ROUTE_BAR_MAX_PX = 100;
  routeBarStats: { routeId: string; locationId: string; description: string; member: number; due: number; onHold: number }[] = [];
  isLoadingRouteBar = false;
  hoveredRouteDesc = '';
  hiddenRouteSeries = new Set<string>();

  get routeGrandTotals(): { member: number; due: number; onHold: number } {
    return this.routeBarStats.reduce(
      (acc, r) => ({ member: acc.member + r.member, due: acc.due + r.due, onHold: acc.onHold + r.onHold }),
      { member: 0, due: 0, onHold: 0 }
    );
  }

  toggleRouteSeries(key: string): void {
    const s = new Set(this.hiddenRouteSeries);
    s.has(key) ? s.delete(key) : s.add(key);
    this.hiddenRouteSeries = s;
  }

  get routeBarMaxTotal(): number {
    let max = 1;
    for (const r of this.routeBarStats) {
      const total =
        (!this.hiddenRouteSeries.has('member') ? r.member : 0) +
        (!this.hiddenRouteSeries.has('due')    ? r.due    : 0) +
        (!this.hiddenRouteSeries.has('onHold') ? r.onHold : 0);
      max = Math.max(max, total);
    }
    return max;
  }

  getRouteSegPx(count: number, key: string): number {
    if (this.hiddenRouteSeries.has(key) || count === 0) return 0;
    return Math.max(3, Math.round((count / this.routeBarMaxTotal) * this.ROUTE_BAR_MAX_PX));
  }

  getRouteVisibleTotal(r: { member: number; due: number; onHold: number }): number {
    return (!this.hiddenRouteSeries.has('member') ? r.member : 0) +
           (!this.hiddenRouteSeries.has('due')    ? r.due    : 0) +
           (!this.hiddenRouteSeries.has('onHold') ? r.onHold : 0);
  }

  openRouteSegDetail(r: { routeId: string; locationId: string; description: string }, seg: 'member' | 'due' | 'onHold'): void {
    const map = {
      member: { statType: 'activeMembers', label: 'Member' },
      due:    { statType: 'dueMembers',    label: 'Due' },
      onHold: { statType: 'membersOnHold', label: 'On Hold' },
    };
    const { statType, label } = map[seg];
    this.openStatDetail(statType, `Route ${r.routeId} · ${label}`, undefined, r.locationId);
  }

  // Stat detail overlay
  showStatDetail = false;
  detailTitle = '';
  detailType = '';
  detailMemberType?: string;
  detailLocationId?: string;

  // ── Runner dashboard state ───────────────────────────────────────────────
  runnerRoutes: any[] = [];
  isLoadingRoutes = false;

  get isManager(): boolean {
    return this.currentUser?.role?.toLowerCase() === 'manager';
  }

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private pageTitle: PageTitleService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.auth.getCurrentUser();
    if (this.isManager) {
      this.pageTitle.set('Dashboard', `Welcome back, ${this.currentUser?.name || 'Runner'} — here are your routes.`);
      this.loadRunnerRoutes();
    } else {
      this.pageTitle.set('Dashboard', `Welcome back, ${this.currentUser?.name || 'User'} — here's your library overview.`);
      this.loadStats();
      this.loadMemberPieStats();
      this.loadTodaySnapshot();
      this.loadRouteBarStats();
    }

    // Returning from Member Master (Back / Save) after Edit or Add was opened
    // from a stat drill-down: reopen that same member list.
    const reopen = history.state?.reopenStatDetail;
    if (reopen?.type) {
      history.replaceState({ ...history.state, reopenStatDetail: undefined }, '');
      this.openStatDetail(reopen.type, reopen.title, reopen.memberType, reopen.locationId);
    }
  }

  loadRunnerRoutes(): void {
    this.isLoadingRoutes = true;
    this.api.getRoutesByRunner(this.currentUser.userID).subscribe({
      next: (data) => { this.runnerRoutes = data || []; this.isLoadingRoutes = false; },
      error: () => { this.isLoadingRoutes = false; },
    });
  }

  ordinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  loadStats(): void {
    this.isLoading = true;
    this.api.getAdminStats().subscribe({
      next: (data) => { this.stats = data; this.isLoading = false; },
      error: () => { this.isLoading = false; }
    });
  }

  loadMemberPieStats(): void {
    this.isLoadingPie = true;
    this.api.getMemberPieStats().subscribe({
      next: (data) => { this.memberPieStats = data; this.isLoadingPie = false; },
      error: () => { this.isLoadingPie = false; }
    });
  }

  loadTodaySnapshot(): void {
    this.isLoadingSnapshot = true;
    this.api.getTodaySnapshot().subscribe({
      next: (data) => { this.todaySnapshot = data; this.isLoadingSnapshot = false; },
      error: () => { this.isLoadingSnapshot = false; }
    });
  }

  canDeactivate(nextUrl?: string): boolean {
    if (nextUrl === '/login') {
      return confirm('You are about to go back to the login page. Continue?');
    }
    return true;
  }

  refresh(): void {
    this.loadStats();
    this.loadMemberPieStats();
    this.loadTodaySnapshot();
    this.loadRouteBarStats();
  }

  loadRouteBarStats(): void {
    this.isLoadingRouteBar = true;
    this.api.getRouteBarStats().subscribe({
      next: (data) => { this.routeBarStats = data || []; this.isLoadingRouteBar = false; },
      error: () => { this.isLoadingRouteBar = false; },
    });
  }

  // ── Pie chart helpers (time-based) ───────────────────────────────────────────

  getPieStat(key: keyof MemberPieStats): MemberTypeStat {
    return this.memberPieStats?.[key] ?? { total: 0, H: 0, L: 0 };
  }

  pieSegments(stat: MemberTypeStat): PieSegment[] {
    if (!stat?.total) return [];
    const C = this.PIE_C;
    const total = stat.total;
    const result: PieSegment[] = [];
    let cumulativeArc = 0;
    const add = (count: number, color: string, label: string) => {
      if (count <= 0) return;
      const arc = (count / total) * C;
      result.push({ dash: `${arc.toFixed(2)} ${(C - arc).toFixed(2)}`, offset: C / 4 - cumulativeArc, color, label, count });
      cumulativeArc += arc;
    };
    add(stat.H || 0, '#f97316', 'Door Delivery');
    add(stat.L || 0, '#8b5cf6', 'At Center');
    return result;
  }

  // ── Bar chart helpers ─────────────────────────────────────────────────────────

  hiddenBarSeries = new Set<'H' | 'L'>();

  toggleBarSeries(type: 'H' | 'L'): void {
    const s = new Set(this.hiddenBarSeries);
    s.has(type) ? s.delete(type) : s.add(type);
    this.hiddenBarSeries = s;
  }

  get barMaxValue(): number {
    let max = 1;
    for (const g of this.BAR_GROUPS) {
      const s = this.getBarStat(g.key);
      if (!this.hiddenBarSeries.has('H')) max = Math.max(max, s.H);
      if (!this.hiddenBarSeries.has('L')) max = Math.max(max, s.L);
    }
    return max;
  }

  getBarStat(key: keyof MemberPieStats): MemberTypeStat {
    return this.memberPieStats?.[key] ?? { total: 0, H: 0, L: 0 };
  }

  getBarPx(key: keyof MemberPieStats, type: 'H' | 'L'): number {
    if (this.hiddenBarSeries.has(type)) return 0;
    const val = this.getBarStat(key)[type] || 0;
    if (val === 0) return 0;
    return Math.max(4, Math.round((val / this.barMaxValue) * this.BAR_MAX_PX));
  }

  getBarVisibleTotal(key: keyof MemberPieStats): number {
    const s = this.getBarStat(key);
    return (!this.hiddenBarSeries.has('H') ? s.H : 0) + (!this.hiddenBarSeries.has('L') ? s.L : 0);
  }

  // ── Stat detail overlay ──────────────────────────────────────────────────────

  openStatDetail(statType: string, title: string, memberType?: string, locationId?: string): void {
    this.detailType = statType;
    this.detailTitle = title;
    this.detailMemberType = memberType;
    this.detailLocationId = locationId;
    this.showStatDetail = true;
  }

  closeStatDetail(): void {
    this.showStatDetail = false;
    this.detailLocationId = undefined;
  }

  /** The open drill-down, so Member Master can bring the user back to it. */
  private currentStatDetail() {
    return { type: this.detailType, title: this.detailTitle, memberType: this.detailMemberType, locationId: this.detailLocationId };
  }

  onEditFromDashboard(member: any): void {
    const returnToStatDetail = this.currentStatDetail();
    this.closeStatDetail();
    this.router.navigate(['/members'], { state: { editMember: member, returnToStatDetail } });
  }

  onAddFromDashboard(): void {
    const returnToStatDetail = this.currentStatDetail();
    this.closeStatDetail();
    this.router.navigate(['/members'], { state: { openAdd: true, returnToStatDetail } });
  }
}
